<template>
  <form
    ref="form"
    class="product-form"
    method="post"
    novalidate
    @submit.prevent="() => {}"
  >
    <transition name="fade">
      <middle-modal
        v-show="showCartModal"
        v-if="isCartModalEnable"
        @close="closeCartModal"
      >
        <template v-slot:header>
          <div class="product-form__header-container">
            <b>{{ $t('product_form.already_added_to_cart') }}</b>
            <div
              v-if="selectedDeviceAndCaseType"
              class="product-form__product-device--correct"
            >
              <div>
                {{ $t('product_form.correct_device') }}
              </div>
            </div>
          </div>
        </template>
        <template v-slot:body>
          <div class="product-form__modal-body-product">
            <img :src="productImage" />
            <div class="product-form__modal-product-in-cart-info">
              <span>{{ product.name }}</span>
              <div class="product-form__info">
                <div class="product-form__price">
                  <span class="hidden" itemprop="price">{{
                    isBlank(currentPromotion) ? displayPrice : currentPrice
                  }}</span>
                  <span class="hidden" itemprop="priceCurrency">{{
                    $store.state.currency
                  }}</span>
                  <span
                    v-if="isBlank(currentPromotion)"
                    id="container__product-price"
                    class="product-price"
                  >
                    <span
                      v-if="currentOldPrice > displayPrice"
                      class="product-price__previous"
                      >{{ formatPrice(currentOldPrice) }}</span
                    >

                    <span
                      v-if="displayPrice"
                      :class="[
                        'product-price__current',
                        'lead',
                        'price',
                        'selling',
                        {
                          'product-price__current--colored':
                            displayPrice < currentOldPrice,
                        },
                      ]"
                      >{{ formatPrice(displayPrice) }}</span
                    >
                  </span>
                  <promotion-event
                    v-else
                    :from-product-page="true"
                    :old-price="parseNumber(currentOldPrice)"
                    :price="parseNumber(currentPrice)"
                    :quantity="parseNumber(currentPromotion.quantity)"
                    :sold="parseNumber(currentPromotion.sold)"
                    :discount="parseNumber(currentPromotion.discount)"
                    :end-date="currentPromotion.end_date"
                    :in-stock="product.in_stock"
                  />
                  <reviews-badge
                    :product-id="product.id"
                    :product-type="productType"
                    :product-name="product.name"
                  />
                </div>

                <div
                  v-if="selectedDeviceName"
                  class="product-form__product-device--selected"
                >
                  <div>
                    {{ $t('product_form.selected_device') }}
                    <span class="product-form__product-device--selected-name">
                      <b>{{ $refs.deviceSelector.selectedDevice.name }}</b>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="shouldDisplayCrossSellItems"
            class="product-form__modal-body-cross-sell"
          >
            <div v-for="item in selectedItems" :key="item.id">
              <div
                class="product-form__subsection-title-cross-sell subsection-title"
              >
                <span>{{ $t('product_form.extra_item') }}</span>
                <line-label :label-text="$t('product_form.extra_item')" />
              </div>
              <div class="product-form__modal-crosssell">
                <div class="product-form__modal-crosssell-in-cart">
                  <img :src="item.productImageCrossSell" />
                  <div class="product-form__modal-crosssell-in-cart-info">
                    <div>{{ item.productNameCrossell }}</div>
                    <div
                      class="product-form__pricing cross-sell-item__pricing--promotion"
                    >
                      <span
                        v-if="
                          item.productOldPriceCrossSell >
                          item.productPriceCrossSell
                        "
                        class="product-form__pricing--previous"
                      >
                        {{ item.productOldPriceCrossSell }}
                      </span>
                      <span class="product-form__pricing--current">
                        {{ item.productPriceCrossSell }}
                      </span>
                    </div>
                    <div
                      v-if="item.productDisplayCaseDevice"
                      class="product-form__product-device--selected"
                    >
                      {{ $t('product_form.selected_device') }}
                      <b>{{ item.productDisplayCaseDevice }}</b>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="hasRecommendationsToShow"
            class="product-form__modal-crossell"
          >
            <recommendation-shelf />
          </div>
        </template>
        <template v-slot:footer>
          <button
            type="button"
            class="btn btn--large btn--green btn--block"
            @click="goToCart"
          >
            {{
              !evaluatedExperiment
                ? $t('product_form.go_to_cart')
                : $t('product_form.go_to_checkout')
            }}
          </button>
          <button
            type="button"
            class="btn btn--discrete btn--small btn--block"
            @click="closeCartModal"
          >
            {{ $t('product_form.continue_purchasing') }}
          </button>
        </template>
      </middle-modal>
    </transition>
    <input
      v-if="product.variant_id && product.variants.length === 0"
      id="variant_id"
      type="hidden"
      name="variant_id"
      :value="product.variant_id"
    />
    <div
      id="inside-product-cart-form"
      itemprop="offers"
      itemscope
      itemtype="https://schema.org/Offer"
    >
      <div class="product-form__sub-title">
        <div v-if="isPlayLunchbag">
          <p>{{ $t('play_devices.lunchbag') }}</p>
        </div>
        <div v-if="isPlayPencilbag">
          <p>{{ $t('play_devices.pencilbag') }}</p>
        </div>
      </div>
      <div v-if="notCustomplacePage" class="productpage__price">
        <span class="hidden" itemprop="price">{{
          isBlank(currentPromotion) ? displayPrice : currentPrice
        }}</span>
        <span class="hidden" itemprop="priceCurrency">{{
          $store.state.currency
        }}</span>

        <span v-if="product.pre_sale_enabled" class="product-form__presale">
          {{ $t('product_form.pre_sale') }}
        </span>
        <span
          v-if="isBlank(currentPromotion)"
          id="container__product-price"
          class="product-price"
        >
          <span
            v-if="currentOldPrice > displayPrice"
            class="product-price__previous"
            >{{ formatPrice(currentOldPrice) }}</span
          >

          <span
            v-if="displayPrice"
            :class="[
              'product-price__current',
              'lead',
              'price',
              'selling',
              {
                'product-price__current--colored':
                  displayPrice < currentOldPrice,
              },
            ]"
            >{{ formatPrice(displayPrice) }}</span
          >
        </span>
        <promotion-event
          v-else
          :from-product-page="true"
          :old-price="parseNumber(currentOldPrice)"
          :price="parseNumber(currentPrice)"
          :quantity="parseNumber(currentPromotion.quantity)"
          :sold="parseNumber(currentPromotion.sold)"
          :discount="parseNumber(currentPromotion.discount)"
          :end-date="currentPromotion.end_date"
          :in-stock="productStock.product_in_stock"
        />
        <reviews-badge
          :product-id="product.id"
          :product-type="productType"
          :product-name="product.name"
        />
      </div>
      <br v-else />

      <clutch-specifications
        v-if="note || product.note"
        :sku="product.sku"
        :material="product.material"
        :note="note || product.note"
      />

      <div
        v-if="showCorporateSalesNote"
        class="corporate-sales-note section--big"
      >
        <b>
          {{ $t('product_form.corporate_sales_note.question') }}
          <a
            href="https://corporativo.gocase.com.br/?utm_source=site&utm_medium=gocase&utm_campaign=tag-pdp"
            target="_blank"
            rel="nofollow"
            style="color: #005baa"
          >
            {{ $t('product_form.corporate_sales_note.contact_here') }}</a
          >*</b
        >
        <p>
          {{ $t('product_form.corporate_sales_note.notice') }}
        </p>
      </div>

      <div
        v-if="onOrderEditor && product.product_type_code === 'case'"
        class="productpage__change-device-info"
      >
        {{ $t('order-editor-notice') }}
      </div>
      <div class="productpage__form">
        <variant-selector
          v-model="variant_id"
          :options="variants"
          option-value-key="id"
        />
        <div v-if="devices.length > 0" class="add-to-cart__casedevice section">
          <div class="composite-device-selector">
            <device-brand-selector
              v-if="!newDeviceSelectorLayout"
              v-show="deviceBrands.length > 1"
              v-model="brand"
              :options="deviceBrands"
            />
            <device-selector
              v-show="devices.length > 0"
              ref="deviceSelector"
              v-model="device_id"
              :new-device-selector-layout="newDeviceSelectorLayout"
              :brands="deviceBrands"
              :options="devices"
              :product="product"
              option-value-key="id"
              required
              :auto-select="false"
              :restored-from-vuex="restoredFromVuex"
              @changedDevice="(device) => (changedDevice = device)"
            />
          </div>

          <div v-if="$store.state.userInteracted && isPresent(device_id)">
            <div v-if="isCase" class="product-form__check-device">
              <b>
                {{ $t('product_form.check_device.check_here') }}
                <a @click.prevent="handleExplainPhoneTypeClick">
                  {{ $t('product_form.check_device.click_here') }}
                </a>
              </b>
            </div>
            <div>
              <transition name="fade">
                <Overlay
                  :visible="showExplainPhoneType"
                  @close="showExplainPhoneType = false"
                />
              </transition>
              <transition name="fade">
                <explain-phone-type-modal
                  v-if="showExplainPhoneType"
                  @closeModal="showExplainPhoneType = false"
                />
              </transition>
            </div>
          </div>

          <case-type-selector
            v-if="
              $store.state.userInteracted &&
              isPresent(device_id) &&
              product.use_case_device
            "
            :product-stock="productStock"
            :variant-id="variant_id || product.variant_id"
            :device-id="device_id"
            :product-id="product.id"
            :is-pre-sale="isPreSale"
            :selected-case-device-id="
              Number(
                caseDeviceFromParams ||
                  product.selected_case_device_id ||
                  case_device_id
              )
            "
            :line-item-editor="lineItemEditor"
            :permitted-case-type-ids="permittedCaseTypeIds"
            :customplace-item="customplaceItem"
            required
            :call-update-data-first-time="callUpdateDataFirstTime"
            @updateCaseTypeId="updateCaseTypeId"
          />

          <case-device-input
            v-model="case_device_id"
            :device-id="device_id"
            :case-type-id="caseTypeId"
            :case-types="flatCaseTypes"
          />
        </div>

        <v-button
          v-if="!$store.state.userInteracted"
          class="product-form__start-btn"
          type="button"
          :label="
            $route.path.match('picture-lineart')
              ? $t('product_form.customize_lineart')
              : isPreSale
              ? $t('product_form.get_on_the_waiting_list')
              : $t('product_form.customize')
          "
          :block="true"
        />

        <input
          id="quantity"
          type="hidden"
          name="quantity"
          value="1"
          class="add-to-cart__quantity"
        />
        <pre-sale-form
          v-if="showPreSale"
          :product-id="product.id"
          :material="material"
          :case-device-id="case_device_id"
          :is-product-page="true"
        />
        <!-- Customization inputs -->
        <template>
          <div
            v-if="
              customizations.length > 0 && product.enable_without_customization
            "
            class="section"
          >
            <div class="subsection-title">
              <hr />
              <span>{{ $t('product_form.customize_product') }}</span>
            </div>
            <label
              v-if="!$store.getters['product/isPrisma']"
              for="without_customization"
              class="pretty-checkbox pretty-checkbox--small section-top section-top--universal"
            >
              <input
                id="without_customization"
                v-model="withoutCustomization"
                type="checkbox"
                name="without_customization"
              />
              <div class="pretty-checkbox__check"></div>
              {{ $t('product_without_customization') }}
            </label>
          </div>

          <line-label
            v-if="showCustomizationOptions"
            :label-text="$t('product_form.customization_options')"
          >
            {{ $t('product_form.customization_options') }}
          </line-label>
          <div
            v-if="showCustomizationOptions"
            class="pretty-select product-form__option-customization-selector"
          >
            <select
              v-model="selectedCustomization"
              class="pretty-select__select required"
            >
              <option
                v-for="custom in customizations"
                :key="custom.engine_identifier"
                :value="custom"
              >
                {{ custom.name }}
              </option>
            </select>
          </div>

          <div
            v-for="custom in adaptedCustomizations"
            v-show="!isPreSale"
            :key="custom.id"
            class="form__row"
          >
            <custom-input
              v-if="
                !product.mystery &&
                (productIsEngine
                  ? custom.customization_type === 'engine'
                  : true)
              "
              v-model="customValues[custom.code]"
              :type="custom.customization_type"
              :custom="custom"
              :material="material"
              :out-of-stock="outOfStock"
              :without-customization="withoutCustomization"
              :line-item-editor="lineItemEditor"
              :mockups="mockups"
              :stock-alert="stockAlert"
              :is-submiting="isSubmiting"
              :restored-from-vuex="restoredFromVuex"
              @quantityUpdated="(quantity) => (quantityUpdated = quantity)"
            />
          </div>
        </template>
        <!-- Add to Cart -->
        <custom-quantity-input
          v-if="!lineItemEditor && false"
          v-show="showAddToCart"
          @quantityUpdated="(quantity) => (quantityUpdated = quantity)"
        />
        <stock-alert
          v-if="stockAlert"
          :product-id="product.id"
          :material="material"
          :case-device-id="case_device_id"
          :start-open="showOutOfStock"
        />
        <!-- For the follow div is necessary keep v-else with the v-show statements -->
        <div
          v-else
          v-show="showAddToCart"
          id="add-to-cart-box"
          class="productpage__flexrow productpage__form-submit"
        >
          <button
            id="add-to-cart-button"
            name="button"
            type="submit"
            class="btn btn--success btn--large btn--block"
            :disabled="isSubmiting"
            :aria-disabled="isSubmiting"
            @click.prevent="onAddToCart"
          >
            <wheel-loading v-if="isSubmiting" color="white" size="22px" />
            <template v-else>
              {{
                lineItemEditor
                  ? isPresent(product.customizations)
                    ? $t('change_customization')
                    : $t('save_changes')
                  : $t('product_form.add_to_cart')
              }}
            </template>
          </button>
          <wishlist-widget v-if="notCustomplacePage" />
        </div>
        <div
          v-show="showCheckboxes && checkboxLoading"
          class="product-form__loading"
        >
          <div style="margin: 0 auto"></div>
        </div>
        <div
          v-show="
            !itemEditor &&
            !lineItemEditor &&
            showCheckboxes &&
            !checkboxLoading &&
            !isPreSale
          "
          class="productpage__cross-sell"
        >
          <div class="subsection-title subsection-title--cross-sell">
            <hr />
            <span>{{ $t('cross_sell.add_items') }}</span>
          </div>
          <cross-sell
            class="section"
            :product-sku="sku"
            :case-device="caseDeviceMaterial"
            :mode="'product'"
            @showcheckboxes="showCheckboxesHandler"
            @crossSellCheckboxChecked="handleCrossSellCheckboxChecked"
            @crossSellCheckboxUnchecked="handleCrossSellCheckboxUnchecked"
          />
        </div>
      </div>
    </div>
    <meta itemprop="itemCondition" content="NewCondition" />
  </form>
</template>

<script>
/* eslint-disable camelcase */

import { mapState, mapGetters } from 'vuex'
import renderRichRelevance from '@/mixins/render-rich-relevance'
import isPresent from '@/utils/isPresent'
import { redirectToCheckout } from '@/utils/toCheckout'
import ExplainPhoneTypeModal from '@/components/ExplainPhoneTypeModal.vue'
import Ga4Parser from '~/utils/Ga4Parser'

import Overlay from '~/components/overlay/Overlay.vue'
import VariantSelector from '~/components/product/form/VariantSelector.vue'
import DeviceBrandSelector from '~/components/product/form/DeviceBrandSelector.vue'
import DeviceSelector from '~/components/product/form/device-selector'
import CustomInput from '~/components/product/form/custom-input'
import CustomQuantityInput from '~/components/product/form/custom-quantity-input/CustomQuantityInput.vue'
import CrossSell from '~/components/cross-sell/CrossSell'
import CaseDeviceInput from '~/components/product/form/case-device-input'
import WishlistWidget from '~/components/product/wishlist-widget/WishlistWidget'
import PromotionEvent from '~/components/product/PromotionEvent'
import ReviewsBadge from '~/components/product/ReviewsBadge'
import VButton from '~/components/v-button/VButton.vue'
import LineLabel from '~/components/line-label/LineLabel.vue'
import PreSaleForm from '~/components/product/form/PreSaleForm.vue'

import appendUrlParam from '~/utils/appendUrlParam'
import debounce from '~/utils/debounce'
import priceable from '~/mixins/priceable'
import pushDataLayer from '~/mixins/gtm/push-data-layer'
import deviceable from '~/mixins/deviceable'
import customizeable from '~/mixins/customizeable'
import errorHandler from '~/mixins/error-handler'
import fixedBlock from '~/mixins/fixed-block'
import RecommendationShelf from '~/components/product-recommendation/RecommendationShelf.vue'

import mockup from '~/mixins/mockup/mockup.js'
import growthBookMixin from '~/mixins/growthbook/growthBookMixin'

const StockAlert = () =>
  import(
    /* webpackChunkName: "stock-alert"  */ '~/components/product/form/StockAlert'
  )

const WheelLoading = () =>
  import(/* webpackChunkName: "wheel-loading"  */ '~/components/loadings/wheel')

const ClutchSpecifications = () =>
  import(
    /* webpackChunkName: "clutch-specifications"  */ '~/components/product/form/clutch-specifications/ClutchSpecifications.vue'
  )

const MiddleModal = () =>
  import(
    /* webpackChunkName: "middle-modal"  */ '~/components/middle-modal/MiddleModal.vue'
  )

const CaseTypeSelector = () =>
  import(
    /* webpackChunkName: "case-type-selector"  */ '~/components/product/form/case-type-selector/CaseTypeSelector.vue'
  )

export default {
  name: 'ProductForm',
  components: {
    MiddleModal,
    VariantSelector,
    DeviceBrandSelector,
    DeviceSelector,
    CaseTypeSelector,
    CustomInput,
    PromotionEvent,
    StockAlert,
    WheelLoading,
    ReviewsBadge,
    CrossSell,
    CaseDeviceInput,
    WishlistWidget,
    ClutchSpecifications,
    VButton,
    LineLabel,
    CustomQuantityInput,
    PreSaleForm,
    ExplainPhoneTypeModal,
    Overlay,
    RecommendationShelf,
  },
  mixins: [
    priceable,
    deviceable,
    customizeable,
    renderRichRelevance,
    errorHandler,
    mockup,
    pushDataLayer,
    fixedBlock,
    growthBookMixin,
  ],
  props: {
    permittedDeviceIds: {
      type: String,
      default: null,
    },
    product: {
      type: Object,
      required: true,
    },
    productStock: {
      type: Object,
      required: true,
    },
    lineItemEditor: {
      type: Boolean,
      default: false,
    },
    lineItemId: {
      type: Number,
      default: null,
    },
    isGift: {
      type: Boolean,
      default: false,
    },
    myOrderNumber: {
      type: [Number, String],
      default: null,
    },
    itemEditor: {
      type: Boolean,
      default: false,
    },
    permittedCaseTypeIds: {
      type: Array,
      default: () => [],
    },
    customplaceItem: {
      type: Object,
      default: null,
    },
    editableOrderProduct: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      variant_id: null,
      isSubmiting: false,
      showCheckboxes: true,
      checkboxLoading: true,
      withoutCustomization: false,
      promotionFromEndpoint: null,
      priceFromEndpoint: null,
      oldPriceFromEndpoint: null,
      computedOptionsPrices: [],
      productType: null,
      note: null,
      changedDevice: null,
      showCartModal: false,
      firstCallCaseDevice: true,
      clickedAddToCart: false,
      newDeviceSelectorLayout: true,
      quantityUpdated: 1,
      selectedCustomization: null,
      appearConfirmDeviceModal: false,
      selectedItems: [],
      showExplainPhoneType: false,
      restoredFromVuex: false,
    }
  },
  computed: {
    isPlayProduct() {
      return !!this.product?.raw_name?.includes('Play')
    },
    isPlayLunchbag() {
      return this.isPlayProduct && this.isDeviceTypePlay('lunchbag')
    },
    isPlayPencilbag() {
      return this.isPlayProduct && this.isDeviceTypePlay('pencilbag')
    },
    shouldDisplayCrossSellItems() {
      return (
        this.selectedItems &&
        this.selectedItems.length > 0 &&
        !this.hasRecommendationsToShow
      )
    },
    hasRecommendationsToShow() {
      const recommendations = this.$store.getters[
        'productRecommendationsStore/recommendations'
      ]

      return this.isPresent(recommendations)
    },
    isUpdateOrder() {
      return this.onOrderEditor && isPresent(this.product.customizations)
    },
    isCartModalEnable() {
      return this.clickedAddToCart && !this.onOrderEditor
    },
    showCorporateSalesNote() {
      return (
        this.product &&
        this.isCorporateProductType(this.product?.product_type_code) &&
        this.$isBrStore
      )
    },
    showCustomizationOptions() {
      return this.customizations && this.customizations.length > 1
    },
    adaptedCustomizations() {
      return this.productIsEngine
        ? this.selectedCustomization
          ? [this.selectedCustomization]
          : this.customizations[0]
        : this.customizations
    },
    productImage() {
      return this.product?.images?.[0]?.url
    },
    adaptedGtmProductId() {
      const cdId = this.caseDevice?.case_device_id
      return cdId ? `${this.product.id}-${cdId}` : this.product.id
    },
    otoGtmProductSku() {
      const cdId = this.caseDevice?.case_device_id
      return cdId ? `${this.product.sku}-${this.material}` : this.product.sku
    },
    productToGtm() {
      return {
        id: this.adaptedGtmProductId, // Esse ID se mantém nos eventos do checkout? Por quê não o SKU?
        name: this.product.name,
        price: this.currentPrice || this.product.price,
        old_price: this.currentOldPrice || this.product.old_price,
        taxonId: this.product.taxon_id,
        product_type_code: this.product.product_type_code,
        image: this.product.images?.[0]?.url,
        ometria_product_id: this.product.ometria_product_id,
        category: this.product.category,
        url: `${window.location.origin}/${this.product.slug}/p`,
        variant_id: this.product.variant_id,
        raw_name: this.product.raw_name,
        currency: this.$store.state.currency,
        oto_sku: this.otoGtmProductSku,
      }
    },
    stockAlert() {
      return (
        !this.product?.pre_sale_enabled &&
        (this.showOutOfStock || this.outOfStock)
      )
    },
    notCustomplacePage() {
      return this.$route?.name !== 'c-uuid'
    },
    callUpdateDataFirstTime() {
      return (
        this.caseDeviceFromParams && !this.$store.state.product.currentPrice
      )
    },
    caseDeviceFromParams() {
      return this.$route.params.case_device?.split('-')?.[0]
    },
    showOutOfStock() {
      return (
        !!this.$route?.query?.['out-of-stock'] &&
        !this.product?.pre_sale_enabled
      )
    },
    onOrderEditor() {
      return this.$route.name === 'update-order-items-number'
    },
    currentOldPrice() {
      if (isPresent(this.caseDevice)) {
        return this.$store.state.product.oldPrice || this.oldPriceFromEndpoint
      } else if (isPresent(this.variants)) {
        return (
          this.$store.state.product.oldPrice || this.product.old_price_with_cost
        )
      } else {
        return this.product.old_price_with_cost
      }
    },
    currentPrice() {
      return this.caseDeviceFromParams
        ? this.$store.state.product.currentPrice || this.priceFromEndpoint
        : this.$store.state.product.currentPrice ||
            this.priceFromEndpoint ||
            this.product.price_with_cost
    },
    currentPromotion() {
      return this.promotionFromEndpoint || this.product?.promotion || {}
    },
    sku() {
      if (this.variant_id) {
        return this.variants.find((v) => v.id === this.variant_id)?.sku
      } else {
        return this.product?.sku
      }
    },
    officialPrice() {
      let price
      if (isPresent(this.currentPromotion) && isPresent(this.currentOldPrice)) {
        price = this.currentOldPrice
      } else {
        price = this.currentPrice
      }

      return Number(price)
    },
    displayPrice() {
      return this.officialPrice
    },
    caseDevice() {
      const currentCaseDeviceId =
        this.device_id ||
        this.getDeviceByCaseTypeAndCaseDeviceId(
          this.caseType,
          this.product.default_case_device_id
          // eslint-disable-next-line camelcase
        )?.device_id

      return this.caseType?.devices?.find(
        (d) => d.device_id === currentCaseDeviceId
      )
    },
    showAddToCart() {
      return !this.isPreSale && (!this.productIsEngine || this.product.mystery)
    },
    isPreSale() {
      return this.product?.pre_sale_enabled
    },
    showPreSale() {
      return this.isPreSale && this.$store.state.userInteracted
    },
    productIsEngine() {
      return this.$store.getters['product/isEngine']
    },
    mockups() {
      // Mixins: Method imported from mockup mixin
      const { code } = this.caseType ? this.caseType : { code: null }
      return this.getArrayFromProduct(this.product, code)
    },
    ...mapState([
      'storeCode',
      'locale',
      'currency',
      'orderNumber',
      'user',
      'substore',
    ]),
    ...mapState('product', ['engineInfo']),
    ...mapGetters('product', ['previewUrl']),
    selectedDeviceName() {
      if (this.devices && this.devices.length > 1) {
        return (
          this.$refs.deviceSelector && this.$refs.deviceSelector.selectedDevice
        )
      }
      return null
    },
    isCase() {
      return this.product?.product_type_code === 'case'
    },
    selectedDeviceAndCaseType() {
      return this.selectedDeviceName && this.isCase
    },
  },
  watch: {
    '$store.state.userInteracted'() {
      this.userInteractedCalls()
    },
    caseTypeId(id) {
      this.$store.commit('product/SET_PERSISTED_CASE_TYPE_ID', id)
    },
    case_device_id(id) {
      this.$store.commit('product/SET_PERSISTED_CASE_DEVICE_ID', id)
    },
    device_id(deviceId) {
      if (!deviceId) return
      const { name: deviceName } = this.devices.find((dv) => dv.id === deviceId)
      if (!deviceName) return
      this.$emit('updateDeviceName', deviceName)
      this.$emit('changeDeviceId', deviceId)
      this.$store.commit('product/SET_PERSISTED_DEVICE_ID', deviceId)
    },
    async caseDevice(cd) {
      if (!cd) return
      if (!this.caseTypes?.length) await this.setProductPrice()

      if (this.firstCallCaseDevice && this.$route.params.case_device) {
        this.firstCallCaseDevice = false
        return
      }

      const prod = Object.assign(JSON.parse(JSON.stringify(this.product)), {
        product_id: this.product.id,
        price: this.currentPrice,
        old_price: this.currentOldPrice,
        promotion: this.currentPromotion,
        case_device_id: cd.case_device_id,
      })

      const ga4Items = new Ga4Parser(this.$store.state).viewItem(prod)

      const ecommerce = new Ga4Parser(this.$store.state).viewItemV2(prod)

      /* 
        Prevent Side Effect since ga4EventValue is an array,    gtm automaticly append a new item instead of create a new array  
      */
      this.pushDataLayer({
        ga4EventValue: undefined,
      })

      this.pushDataLayer({
        event: 'genericEvent',
        eventCategory: 'navigation',
        eventAction: 'changedCaseDevice',
        eventValue: this.productToGtm,
        ecommerce,
        ga4EventValue: ga4Items,
      })
    },
    withoutCustomization(newBool) {
      this.$store.dispatch('product/setWithoutCustomization', newBool)
    },
    sku(newSku) {
      this.$store.commit('product/SET_SKU', newSku)
    },
    material(newMaterial) {
      this.$store.commit('product/SET_MATERIAL', newMaterial)
      if (this.$nuxt.context.from) {
        this.setProductType(newMaterial)
        this.setDescription(newMaterial)
      } else {
        this.setProductType(newMaterial)
        this.setDescription(newMaterial)
      }
    },
    customValues: {
      deep: true,
      handler(newValues) {
        this.$store.commit('product/UPDATE_CUSTOM_VALUES', newValues)
        if (newValues.stamp) {
          this.warnIfNoMaterialSelected()
        }
      },
    },
    '$store.state.globalDevice'(newGlobalDevice, oldGlobalDevice) {
      if (
        newGlobalDevice &&
        oldGlobalDevice &&
        newGlobalDevice.slug !== oldGlobalDevice.slug
      )
        this.setCaseDevice(newGlobalDevice.case_device_id)
    },
  },

  created() {
    this.selectedCustomization = this.customizations[0]
  },

  mounted() {
    ;(async () => {
      await this.initializeGrowthBook('modalToCheckout')
      this.$store.dispatch(
        'growthbook/updateEvaluatedExperiment',
        this.evaluatedExperiment
      )
    })()
    if (this.$store.state.product.restoreFromGoogle) {
      this.material = this.$store.state.product.persistedMaterial
      this.device_id = this.$store.state.product.persistedDeviceId
      this.caseTypeId = this.$store.state.product.persistedCaseTypeId
      this.case_device_id = this.$store.state.product.persistedCaseDeviceId

      this.$store.dispatch('product/resetRestoreFromGoogle')
      this.restoredFromVuex = true
    }
    // if (
    //   window.testNewDeviceSelectorLayout ||
    //   this.$route.query.new_device_selector ||
    //   sessionStorage.getItem('new_device_selector')
    // ) {
    //   this.newDeviceSelectorLayout = true
    //   sessionStorage.setItem('new_device_selector', true)
    // }
    if (this.product) {
      this.setProductType()
      if (this.$store.state.userInteracted) {
        this.userInteractedCalls()
      }
    }
    this.$emit('productFormLoaded')
    const savedImageInputs =
      JSON.parse(localStorage.getItem('imageInputs')) ?? []
    if (Array.isArray(savedImageInputs) && savedImageInputs.length > 0) {
      localStorage.setItem('imageInputs', '[]')
    }
  },

  async fetch() {
    this.customValues.stamp = null
    this.$emit('displayPrice', this.officialPrice)

    this.brand = this.deviceBrands?.[0]
    if (this.devices.length === 1 && !this.device_id)
      this.device_id = this.devices.length === 1 ? this.devices[0].id : null

    // Fill Store init state
    this.$store.commit('product/SET_SKU', this.sku)
    this.$store.commit('product/SET_MATERIAL', this.material)

    this.setCaseDevice(
      Number(
        // eslint-disable-next-line camelcase
        this.caseDeviceFromParams || this.product.selected_case_device_id
      )
    )

    if (this.caseDeviceFromParams) await this.setProductPrice()
  },
  methods: {
    isDeviceTypePlay(type) {
      const codeMap = {
        lunchbag: 'lancheiraplay',
        pencilbag: 'estojoplay',
      }
      return this.devices?.[0]?.code === codeMap[type]
    },
    handleExplainPhoneTypeClick() {
      this.showExplainPhoneType = true
      this.sendExplainPhoneTypeClickToGTM()
    },
    sendExplainPhoneTypeClickToGTM() {
      this.pushDataLayer({
        event: 'genericEvent',
        eventCategory: 'navigation',
        eventAction: 'explain_phone_type_click',
      })
    },
    handleCrossSellCheckboxChecked(crossSellItem) {
      this.$set(this.selectedItems, this.selectedItems.length, crossSellItem)
    },
    handleCrossSellCheckboxUnchecked(crossSellItem) {
      const index = this.selectedItems.findIndex(
        (item) => item.id === crossSellItem.id
      )
      if (index !== -1) {
        this.$set(this.selectedItems, index, null)
        this.selectedItems = this.selectedItems.filter((item) => item !== null)
      }
    },
    isPresent,
    userInteractedCalls() {
      const isGroupedProduct =
        this.product?.grouped_products?.length ||
        this.$route.query.grouped_products

      if (
        this.$device.isDesktop &&
        window.innerWidth > 1023 &&
        !isGroupedProduct
      ) {
        this.startFixedBlock(
          '.productpage__wrapper',
          '.productpage__largeimage'
        )
      }

      setTimeout(() => {
        const prod = Object.assign(JSON.parse(JSON.stringify(this.product)), {
          product_id: this.product.id,
          price: this.currentPrice,
          old_price: this.currentOldPrice,
          promotion: this.currentPromotion,
          case_device_id: this.product.default_case_device_id,
        })
        const ga4Items = new Ga4Parser(this.$store.state).viewItem(prod)

        const ecommerce = new Ga4Parser(this.$store.state).viewItemV2(prod)

        /* 
          Prevent Side Effect since ga4EventValue is an array, 
          gtm automaticly append a new item instead of create a new array  
        */
        this.pushDataLayer({
          ga4EventValue: undefined,
        })

        this.pushDataLayer({
          event: 'genericEvent',
          eventCategory: 'navigation',
          eventAction: 'viewProduct',
          eventValue: this.productToGtm,
          ecommerce,
          ga4EventValue: ga4Items,
        })
      }, 3000)
    },
    closeCartModal() {
      this.showCartModal = false
      this.clickedAddToCart = false
      document
        .querySelector('.shelf.shelf--no-crossell')
        ?.scrollIntoView({ behavior: 'smooth' })
    },
    updateCaseTypeId(ctId) {
      this.caseTypeId = ctId
    },
    async newComputedOptionsPrices(computedOptionsPrices) {
      this.computedOptionsPrices.push(...computedOptionsPrices)
      await this.setProductPrice()
    },
    async setProductPrice() {
      if (!this.caseDevice) return
      const { case_device_id: caseDeviceId } = this.caseDevice
      try {
        let response = this.computedOptionsPrices?.find(
          // eslint-disable-next-line camelcase
          (cop) => cop.case_device_id === caseDeviceId
        )

        if (!caseDeviceId) return

        if (!response) {
          response = await this.$axios.$get('/products/price', {
            params: {
              ...this.$store.getters.storeInfo,
              case_device_id: caseDeviceId,
              variant_id: this.variant_id || this.product.variant_id,
              product_id: this.product.id,
            },
          })

          this.computedOptionsPrices.push({
            case_device_id: caseDeviceId,
            price: response.price,
            old_price: response.old_price,
            promotion: response.promotion,
          })
        }

        this.promotionFromEndpoint = response.promotion
        this.priceFromEndpoint = response.price
        this.oldPriceFromEndpoint = response.old_price

        if (response.promotion) {
          this.$store.dispatch(
            'product/setCurrentPromotion',
            response.promotion
          )
        }

        if (response?.price || response?.old_price) {
          this.$store.dispatch('product/setCurrentPrice', response?.price)
          this.$store.dispatch('product/setOldPrice', response?.old_price)
        }
      } catch (err) {
        this.$sentry.captureException(err.response || err)
      }
    },
    /**
     * Remember User to choose an Device
     */
    /* istanbul ignore next */
    warnIfNoMaterialSelected: debounce(function () {
      if (
        this.isBlank(this.material) &&
        this.product.product_type_code === 'case'
      ) {
        this.$displayTopMessage(this.$t('product_form.choose_device'))
      }
    }, 1000),
    /**
     * Validate all form inputs
     *
     * @returns false if any input has errors
     */
    validateInputs() {
      const isValidatable = (input) => typeof input.validate === 'function'
      const inputValidations = this.$children
        .filter(isValidatable)
        .map((input) => input.validate())

      return !inputValidations.some((validation) => validation.hasErrors)
    },

    emitGtagEvent(name, params) {
      this.$store.dispatch('analytics/emitGtagEvent', {
        name,
        products: [
          {
            ...this.product,
            price: this.currentPrice,
            case_device_id: this.case_device_id,
          },
        ],
        ...params,
      })
    },

    onAddToCart() {
      this.emitGtagEvent('click_add_product', {
        has_item_editor: this.itemEditor,
      })
      if (this.itemEditor) {
        if (this.customplaceItem) {
          this.$emit('updateItemProduct', {
            product_id: this.product.id,
            case_device_id: this.case_device_id,
            engine_information: {
              ...this.customValues,
              without_customization: this.withoutCustomization,
            },
          })
        } else {
          const itemProductCustomization = []
          for (const cv in this.customValues) {
            itemProductCustomization.push({
              code: cv, // code is required
              value: this.customValues[cv],
            })
          }
          this.$emit('updateItemProduct', {
            case_device_id: this.case_device_id,
            customizations: itemProductCustomization,
          })
        }
      } else {
        this.submit()
        this.richInitializer(() => {
          this.addToCartRichTracker(this.product)
        })
      }
    },

    areAllImageInputs() {
      const totalNumberOfImageInputs = this.$el.querySelectorAll(
        `#spree-e-inputs input[type='file']`
      ).length
      const savedImageInputs = JSON.parse(localStorage.getItem('imageInputs'))
      const numberOfSavedImageInputs = savedImageInputs?.length ?? 0
      return numberOfSavedImageInputs === totalNumberOfImageInputs
    },

    submit() {
      this.clickedAddToCart = true
      if (!this.areAllImageInputs()) {
        this.$displayTopMessage(this.$t('product_form.upload_files_inputs'), {
          type: 'error',
        })
        return
      }
      if (this.validateInputs()) {
        this.emitGtagEvent('adding_to_cart')
        if (this.evaluatedExperiment) {
          this.emitGtagEvent('adding_to_cart_with_jump')
        }
        this.startSubmit()
        this.initializeProductRecommendationsStore(this.product?.id)
        return this.addToCart()
          .then(this.parseErrorResponse)
          .then(this.parseSuccessResponse)
          .then(this.triggerDataLayer)
          .then(this.setOrderAttrs)
          .then(this.redirectToCartOnSuccess)
          .then(this.alreadyOnCartSubmit)
          .catch(this.onError)
          .finally(this.endSubmit)
      }
    },

    initializeProductRecommendationsStore(productId) {
      const baseCustomizationUrlElement = document.querySelector(
        '.engine-input #input_engine_value'
      )
      const baseCustomizationUrl = baseCustomizationUrlElement?.value

      if (baseCustomizationUrl) {
        this.$store.dispatch('productRecommendationsStore/initializeStore', {
          baseCustomizationUrl,
          productId,
        })
      }
    },

    addToCart() {
      /**
       * this if means that is to edit instead populate when current product form
       * is from line item editor and the product price is 0 (maybe it be a gift)
       */
      if (
        this.lineItemEditor &&
        (!Number(this.currentPrice) || this.isGift || this.onOrderEditor)
      ) {
        return this.updateEditableLineItem()
      } else {
        return this.$axios.$post('/carts/populate', this.getFormData())
      }
    },

    goToCart() {
      this.emitGtagEvent('go_to_cart')
      if (this.evaluatedExperiment) {
        this.emitGtagEvent('jump_to_checkout')
        setTimeout(() => {
          redirectToCheckout({ ...this.$config, ...this.$store.state })
        }, 9)
        return
      }
      setTimeout(() => {
        this.$router.push('/cart')
      }, 9)
    },

    async updateEditableLineItem() {
      try {
        const params = {
          ...this.$store.getters.storeInfo,
          number: this.myOrderNumber || this.orderNumber,
        }

        const product = await this.$axios.$get(
          this.onOrderEditor ? 'orders/edit_line_item' : 'carts/edit_line_item',
          {
            params: {
              ...params,
              line_item_id: this.lineItemId,
            },
          }
        )

        const lnCustomizations = []

        for (const cv in this.customValues) {
          const custom = product.customizations.find((cus) => cus.code === cv)
          if (custom) {
            lnCustomizations.push({
              id: custom.line_item_customization.id,
              code: cv, // code is required
              value: this.customValues[cv],
            })
          }
        }

        await this.$axios.$post(
          appendUrlParam(
            this.onOrderEditor
              ? 'orders/update_line_items'
              : 'carts/update_contents',
            params
          ),
          {
            order: {
              line_items_attributes: [
                {
                  id: this.lineItemId,
                  line_item_customizations_attributes: lnCustomizations,
                  case_device_id: this.case_device_id,
                },
              ],
            },
          }
        )

        if (this.isUpdateOrder) {
          this.$displayTopMessage(this.$t('product_form.change_saved'))
        }

        window.location.reload()
      } catch (err) {
        this.$sentry.captureException(err)
        this.handleError(err)
        if (this.isUpdateOrder) {
          this.$displayTopMessage(this.$t('product_form.change_unsaved'), {
            type: 'error',
          })
        }
      }
    },
    async setProductType(material) {
      // eslint-disable-next-line camelcase
      if (!this.caseDevice?.case_device_id && !material) {
        this.productType = this.product.stamped_product_type
        this.$emit('updateProductType', this.productType)
        return
      }
      const res = await this.$axios.$get('/products/stamped_product_type', {
        params: {
          ...this.$store.getters.storeInfo,
          // eslint-disable-next-line camelcase
          case_device_id: this.caseDevice?.case_device_id,
          product_id: this.product.id,
        },
      })
      this.productType = res.product_type
      this.$emit('updateProductType', this.productType)
    },
    async setDescription(material) {
      // eslint-disable-next-line camelcase
      if (!this.caseDevice?.case_device_id && !material) {
        this.description = this.product.description
        this.note = this.product.note
        this.$emit('updateDescription', this.description)
        return
      }
      const res = await this.$axios.$get('/products/description', {
        params: {
          ...this.$store.getters.storeInfo,
          // eslint-disable-next-line camelcase
          case_device_id: this.caseDevice?.case_device_id,
          product_id: this.product.id,
        },
      })
      this.description = res.description
      this.note = res.note
      this.$emit('updateDescription', this.description)
    },
    getFormData() {
      const formData = new FormData(this.$refs.form)
      const extraData = [
        ['store', this.storeCode],
        ['locale', this.locale],
        ['currency', this.currency],
        // ['quantity', this.quantityUpdated],
        ['number', this.orderNumber],
        ['user_id', this.user?.id],
        ['image_url', this.previewUrl],
        ['engine_information', this.engineInfo],
      ]

      if (this.$route.query.customization_base)
        extraData.push(['added_by_suggestion', true])
      if (this.substore.active) extraData.push(['substore', this.substore.code])

      extraData
        .filter(([_, value]) => this.isPresent(value))
        .forEach(([key, value]) => {
          formData.append(key, value)
        })

      return formData
    },
    parseErrorResponse(data) {
      if (data?.error) throw data.error

      return data
    },
    parseSuccessResponse(data) {
      // eslint-disable-next-line camelcase
      if (data?.order_number) {
        return {
          number: data.order_number,
          quantity: data.order_quantity,
        }
      }

      return null
    },
    triggerDataLayer(order) {
      const prod = {
        ...JSON.parse(JSON.stringify(this.product)),
        product_id: this.product.id,
        price: this.currentPrice,
        promotion: this.currentPromotion,
        case_device_id: this.caseDevice?.case_device_id,
        old_price: this.currentOldPrice,
      }

      const ga4Items = new Ga4Parser(this.$store.state).addToCart(prod)

      const ecommerce = new Ga4Parser(this.$store.state).addToCartV2(prod)

      /* 
        Prevent Side Effect since ga4EventValue is an array, 
        gtm automaticly append a new item instead of create a new array  
      */
      this.pushDataLayer({
        ga4EventValue: undefined,
      })

      this.pushDataLayer({
        event: 'genericEvent',
        eventCategory: 'interactWithPage',
        eventAction: 'clickOnButton',
        eventLabel: 'addToCart',
        eventValue: {
          currency: this.currency,
          product: {
            id: this.adaptedGtmProductId,
            name: this.product.name,
            price: this.currentPrice,
            sku: this.product.sku,
            has_customization: this.customizations.length > 0,
            variant_id: this.product.variant_id,
            raw_name: this.product.raw_name,
          },
        },
        ecommerce,
        ga4EventValue: ga4Items,
      })

      if (this.isBlank(order)) return
      return order
    },
    setOrderAttrs(order) {
      if (this.isBlank(order)) return

      this.$store.commit('SET_ORDER_NUMBER', order.number)
      this.$store.commit('SET_CART_QTY', order.quantity)
    },
    redirectToCartOnSuccess() {
      const sideModal = document.querySelector(
        '#modal-engines > div.modal-velociraptor__title > div'
      )
      if (sideModal) sideModal.click()

      if (this.$cartModalIsActive) {
        this.showCartModal = true
      } else if (this.onOrderEditor) window.location.reload()
      else if (this.$router) this.$router.push('/cart')
    },
    alreadyOnCartSubmit() {
      if (this.$route.name === 'cart') {
        this.$emit('updatedLineItem')
      }
    },
    onError(msg) {
      this.showCartModal = false
      this.$emit('error', msg)
    },
    startSubmit() {
      this.isSubmiting = true
    },
    endSubmit() {
      this.isSubmiting = false
    },
    showCheckboxesHandler(shouldShow) {
      this.showCheckboxes = shouldShow
      if (shouldShow) {
        this.checkboxLoading = true
        clearTimeout(this.checkboxTimeout)
        this.checkboxTimeout = setTimeout(() => {
          this.checkboxLoading = false
          clearTimeout(this.checkboxTimeout)
        }, 500)
      }
    },
    isCorporateProductType(productTypeCode) {
      const corporateProductTypes = [
        'garrafaebook',
        'copoebook',
        'copo',
        'garrafa',
        'backpackbts',
        'backpackcd',
        'mochilarodinhabts',
        'clutchcd',
        'clutch',
        'basegarrafacd',
        'lunchbag',
        'travelbag',
        'travelbagcd',
        'bags',
        'totebagcd',
        'travelcasecd',
        'estojobts',
        'necessaire',
      ]
      return corporateProductTypes.includes(productTypeCode)
    },
  },
}
</script>
<style lang="scss">
@import '~/assets/css/subsection-title.scss';
@import '~/assets/css/floating-label.scss';
@import '~/assets/css/form.scss';
@import '~/assets/css/_catalog.scss';

.product-form__header-container {
  display: flex;
  flex-direction: column;
}

.product-form__product-device--correct {
  font-size: 12px;
  color: $v4_color_primary;
  margin-right: 5px;
}

.product-form__product-device--selected {
  font-size: 13px;
  margin-bottom: 12px;
}

.product-form__option-customization-selector {
  margin-bottom: 20px;
}

#e-app .e-footer {
  // TODO: remove this code and add in the relative engine
  bottom: 20px !important;
}

.productpage__change-device-info {
  margin-bottom: 25px;
  color: #ef88bf;
}

.corporate-sales-note {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  background-color: #f7f9fc;
  border-radius: 4px;
  padding: 10px 14px;

  p {
    font-size: 11px;
    font-style: italic;
    margin: 0px;
  }
}

.product-form__modal-body-product {
  display: flex;
  margin-bottom: 10px;
  img {
    width: 80px;
    height: 80px;
    margin-right: 5px;
  }
}

.product-form__check-device {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px;
  margin-top: 10px;
  text-align: center;

  b,
  a {
    display: block;
  }

  a {
    color: $v4_color_primary;
    text-decoration: underline;
  }
}

.product-form__presale {
  background-color: #94d6da;
  color: #fff;
  font-size: 11px;
  padding: 5px 5px;
  border: none;
  border-radius: 2px;
  cursor: pointer;
  text-transform: uppercase;
  transition: background-color 0.3s ease;
  margin-right: 5px;
  font-weight: bold;
}

.product-form__loading {
  min-height: 162px;
  display: flex;
  text-align: center;
  align-items: center;
}

.product-price__current--colored {
  color: $v4_color_primary;
}

.productpage__form-submit {
  margin-top: 20px;
}

.product-form__modal-crosssell {
  display: flex;
  margin-top: 10px;

  img {
    width: 80px;
    height: 80px;
    margin-right: 5px;
  }
}

.product-form__modal-crosssell-in-cart {
  display: flex;
  flex-direction: row;
}

.product-form__modal-product-in-cart-info,
.product-form__modal-crosssell-in-cart-info {
  width: 100%;
}

.product-form__pricing--previous {
  text-decoration: line-through;
  margin-right: 5px;
}

.product-form__pricing--current {
  color: $v4_color_primary;
}

@media (min-width: $v4_desktop) {
  .productpage__form {
    .productpage__form-submit,
    .productpage__flexrow.productpage__flexrow--lite {
      margin-top: 30px;
    }
  }

  .product-form__presale {
    font-size: 13px;
    padding: 5px 10px;
  }
}

// fix temporário até dj voltar e arrumar na engine
.input-component {
  line-height: 0px !important;
}

.christmas-message {
  border-radius: 4px;
  padding: 10px 14px;
  background: #fdf7fa;
}
.christmas-message p {
  margin: 5px 0 0;
  font-size: 13px;
  font-weight: bold;
}

.product-form__price,
.productpage__price {
  min-height: 26px;
}

.casetype-selector > div,
.lite-form-component > div {
  visibility: visible;
  animation: fadeIn 1s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}
</style>
